import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight, Phone, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo } from '../data/mock';
import ServiceAreaMap from '../components/ServiceAreaMap';

export const ServiceAreas = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Service Areas - Mobile Detailing Redlands, Loma Linda & Yucaipa | Precision Mobile Detailing';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional mobile car detailing serving Redlands, Loma Linda, and Yucaipa, CA. We bring premium auto detailing directly to your home or office.');
    }
  }, []);

  const serviceAreas = [
    {
      city: 'Redlands',
      state: 'CA',
      slug: 'redlands-ca',
      description: 'Serving downtown Redlands, the University of Redlands area, and surrounding neighborhoods with premium mobile detailing services.',
      image: 'https://dynamic-media-cdn.tripadvisor.com/media/photo-o/2b/17/14/48/caption.jpg?w=1200&h=-1&s=1',
      highlights: ['University District', 'Historic Downtown', 'South Redlands']
    },
    {
      city: 'Loma Linda',
      state: 'CA',
      slug: 'loma-linda-ca',
      description: 'Mobile detailing for the Loma Linda University area, medical campus, and residential communities throughout the city.',
      image: 'https://i.ytimg.com/vi/38EWcpWsBfQ/maxresdefault.jpg',
      highlights: ['Medical Center Area', 'University Campus', 'Residential Districts']
    },
    {
      city: 'Yucaipa',
      state: 'CA',
      slug: 'yucaipa-ca',
      description: 'Bringing professional car detailing to Yucaipa homes and businesses, from the foothills to Chapman Heights.',
      image: 'https://images.unsplash.com/photo-1649042250037-122e56bc16b1?w=800&q=80',
      highlights: ['Chapman Heights', 'Wildwood Canyon', 'Oak Glen Area']
    },
    {
      city: 'Rancho Cucamonga',
      state: 'CA',
      slug: 'rancho-cucamonga-ca',
      description: 'Premium mobile detailing services for Rancho Cucamonga residents, from Victoria Gardens to Alta Loma and Etiwanda.',
      image: 'https://s3-media0.fl.yelpcdn.com/bphoto/w37ny2tARRZ4ODGq3div3g/348s.jpg',
      highlights: ['Victoria Gardens', 'Alta Loma', 'Etiwanda']
    },
    {
      city: 'Riverside',
      state: 'CA',
      slug: 'riverside-ca',
      description: 'Mobile car detailing throughout Riverside, including Downtown, Mission Inn area, Canyon Crest, and UCR campus.',
      image: 'https://cdn.britannica.com/05/152405-050-EABDBE62/Riverside-California.jpg',
      highlights: ['Downtown Riverside', 'Mission Inn District', 'Canyon Crest']
    },
    {
      city: 'San Bernardino',
      state: 'CA',
      slug: 'san-bernardino-ca',
      description: 'Professional mobile detailing delivered across San Bernardino, from CSUSB campus to Shandin Hills and beyond.',
      image: 'https://images.unsplash.com/photo-1668279200700-b7dac51b0b83?w=800&q=80',
      highlights: ['CSUSB Area', 'Shandin Hills', 'Downtown']
    },
    {
      city: 'Ontario',
      state: 'CA',
      slug: 'ontario-ca',
      description: 'Premium mobile detailing for Ontario residents, from Ontario Airport and Victoria Gardens to residential neighborhoods.',
      image: 'https://images.unsplash.com/photo-1580494766979-ccfecebfbc8a?w=800&q=80',
      highlights: ['Ontario Airport', 'Victoria Gardens', 'East Ontario']
    },
    {
      city: 'Corona',
      state: 'CA',
      slug: 'corona-ca',
      description: 'Mobile car detailing throughout Corona, including Eagle Glen, South Corona, and Downtown Corona.',
      image: 'https://images.unsplash.com/photo-1499310226026-b9d598980b90?w=800&q=80',
      highlights: ['Eagle Glen', 'South Corona', 'Downtown Corona']
    },
    {
      city: 'Fontana',
      state: 'CA',
      slug: 'fontana-ca',
      description: 'Professional mobile detailing serving Fontana, from North Fontana and Sierra Lakes to Downtown Fontana.',
      image: 'https://images.unsplash.com/photo-1642212436181-bcc5960f7cd4?w=800&q=80',
      highlights: ['North Fontana', 'Sierra Lakes', 'Downtown Fontana']
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-6">
              <MapPin className="w-6 h-6 text-black" />
              <span className="text-sm font-semibold uppercase tracking-wider">Where We Serve</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Professional Mobile Detailing Across the <span className="text-black">Inland Empire</span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              We bring premium car detailing directly to your driveway, parking lot, or workplace. 
              No need to drive anywhere—experience showroom results at your location.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="cta-button text-lg px-8 py-6">
                <Link to="/booking">
                  Book Your Detail <ArrowRight className="ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6">
                <a href={`tel:${businessInfo.phone}`}>
                  <Phone className="mr-2" /> {businessInfo.phone}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Mobile Detailing */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Why Choose Mobile Detailing?</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3">Save Time</h3>
                <p className="text-gray-600">No driving to a shop and waiting around. We come to you while you work, relax, or run errands.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3">Professional Results</h3>
                <p className="text-gray-600">Premium products and expert techniques delivered with the same quality as high-end detailing shops.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3">Ultimate Convenience</h3>
                <p className="text-gray-600">Home, office, or job site—wherever you are, that's where we detail your vehicle.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Service Area Map */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <ServiceAreaMap height="600px" showTitle={true} />
          </div>
        </div>
      </section>

      {/* Service Area Cities */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Cities We Serve</h2>
              <p className="text-xl text-gray-600">Premium mobile auto detailing delivered to your location</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {serviceAreas.map((area) => (
                <Card key={area.slug} className="service-card overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300">
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={area.image} 
                      alt={`Mobile car detailing in ${area.city}, ${area.state}`}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-2xl font-bold mb-3">{area.city}, {area.state}</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">{area.description}</p>
                    
                    <div className="mb-6">
                      <h4 className="font-semibold mb-2 text-sm text-gray-500 uppercase">Areas We Cover:</h4>
                      <div className="flex flex-wrap gap-2">
                        {area.highlights.map((highlight, idx) => (
                          <span key={idx} className="text-sm bg-gray-100 px-3 py-1 rounded-full">
                            {highlight}
                          </span>
                        ))}
                      </div>
                    </div>

                    <Button asChild className="w-full cta-button">
                      <Link to={`/service-areas/${area.slug}`}>
                        View {area.city} Services <ArrowRight className="ml-2 w-4 h-4" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Experience Mobile Detailing?</h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto text-gray-300">
            Book your appointment today and see why customers across the Inland Empire trust Precision Mobile Detailing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-black hover:bg-gray-100 text-lg px-8 py-6">
              <Link to="/booking">
                Book Now <ArrowRight className="ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 border-white text-white hover:bg-white hover:text-black transition-all duration-300">
              <Link to="/services">
                View Services
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceAreas;
